import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  saveExecSummary,
  generateExecSummary,
  DEFAULT_META,
} from '../data/metaApi.js'
import { useHistoryStore } from '../store/historyStore.js'

/**
 * useSaveExecSummary — save (or clear) the execSummary slot on meta via
 * the server-side partial update. Optimistic cache write; rolls back on
 * error.
 *
 * Usage:
 *   const save = useSaveExecSummary()
 *   save.mutate({ tldr, flagsSummary, generatedAt, generatedBy, edited: true })
 *   save.mutate({ clear: true })   // wipes the summary
 */
export function useSaveExecSummary() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (payload) => {
      const execSummary = await saveExecSummary(payload)
      return execSummary
    },

    onMutate: async (payload) => {
      await queryClient.cancelQueries({ queryKey: ['meta'] })
      const previous = queryClient.getQueryData(['meta']) || DEFAULT_META

      useHistoryStore
        .getState()
        .pushSnapshot(
          'Exec summary',
          queryClient.getQueryData(['goals']),
          previous
        )

      const nextExec = payload?.clear ? null : payload
      queryClient.setQueryData(['meta'], {
        ...previous,
        execSummary: nextExec,
      })
      return { previous }
    },

    onSuccess: (execSummary) => {
      const current = queryClient.getQueryData(['meta']) || DEFAULT_META
      queryClient.setQueryData(['meta'], { ...current, execSummary })
    },

    onError: (err, vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(['meta'], ctx.previous)
    },
  })
}

/**
 * useGenerateExecSummary — POST to /ai-summary and receive a fresh
 * { tldr, flagsSummary } payload. Does NOT auto-save; the component
 * decides whether to call useSaveExecSummary after the user reviews.
 */
export function useGenerateExecSummary() {
  return useMutation({
    mutationFn: async ({ weeklySummaries, aiPreferences }) =>
      generateExecSummary({ weeklySummaries, aiPreferences }),
  })
}
